<script setup>
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useNumberFormat } from "@/composables/useNumberFormat";
import BarStack from "./BarStack.vue";

const { t, locale } = useI18n();

const intlLocale = locale.value == "fr" ? "fr-CA" : "en-us";
const { dec2, formatNumberWithSuffix, comma } = useNumberFormat(intlLocale);

const props = defineProps({
    type: {
        type: String,
        default: "52week",
        validator: value => ["52week", "day", "volume"].includes(value),
    },
    chartColors: {
        type: Object,
        default: () => ({
            primary: "#0477D1",
            secondary: "#6D737A",
            emptybar: "#F6F6F6",
            current: "#333333",
            border: "#E3E3E3",
        }),
    },
    high: { type: Number, default: null },
    low: { type: Number, default: null },
    current: { type: Number, default: null },
    avg: { type: Number, default: null },
    title: { type: String, default: null },
    description: { type: String, default: null },
});

const chartContainer = ref(null);
const popoverVisible = ref(false);
const popoverPosition = ref({ top: 0, left: 0 });

const volChart = computed(() => props.type === "volume" && props.current && props.avg);
const isValidData = computed(() => {
    return (props.high && props.low && props.current) || (props.current && props.avg);
});

const highEqualsLow = computed(() => {
    return props.high && props.low && props.high.toFixed(2) === props.low.toFixed(2);
});

function showPopover(event) {
    if (event && event.clientY !== undefined && event.clientX !== undefined) {
        popoverVisible.value = true;
        popoverPosition.value = { top: event.clientY + 10, left: event.clientX + 10 };
    }
}

function hidePopover() {
    popoverVisible.value = false;
}
</script>

<template lang="pug">
.qmodii-range-chart-cont
    .qmodii-range-chart-header
        h4.qmodii-range-chart-heading {{ props.title }}
        p.qmodii-range-chart-description.qmodii-secondary-text(v-if="isValidData") {{ props.description }}
    .qmodii-range-chart(
        v-if="isValidData && !highEqualsLow",
        ref="chartContainer",
        :class="`qmodii-${props.type}-chart`"
    )
        .qmodii-bar-container(v-if="props.type === '52week' || props.type === 'day'")
            BarStack(
                :current="props.current",
                :high="props.high",
                :low="props.low",
                :chartColors="props.chartColors",
                @mouseover="showPopover($event)",
                @mouseleave="hidePopover"
            )
            .qmodii-labels
                .qmodii-label.qmodii-label-low
                    span.qmodii-label-text {{ t("Low") }}
                    span.qmodii-label-text {{ dec2(props.low) }}
                .qmodii-label.qmodii-label-high
                    span.qmodii-label-text {{ t("High") }}
                    span.qmodii-label-text {{ dec2(props.high) }}
        .qmodii-bar-container(v-else-if="props.type === 'volume'")
            BarStack(
                :type="props.type",
                :current="props.current",
                :avg="props.avg",
                :chartColors="props.chartColors",
                @mouseover="showPopover($event)",
                @mouseleave="hidePopover"
            )
            .qmodii-legend
                .qmodii-legend-item
                    .qmodii-legend-icon(:style="{ backgroundColor: props.chartColors.secondary }")
                    span.qmodii-legend-text {{ t("Average") }}: {{ formatNumberWithSuffix(props.avg) }}
                .qmodii-legend-item
                    .qmodii-legend-icon(:style="{ backgroundColor: props.chartColors.primary }")
                    span.qmodii-legend-text {{ t("Current") }}: {{ formatNumberWithSuffix(props.current) }}
    p.qmodii-secondary-text(v-else) {{ t("nodataavailable") }}
    .qmodii-popover(
        v-if="popoverVisible",
        :style="{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }"
    )
        .qmodii-popover-content(v-if="props.type === 'volume'")
            .qmodii-popover-item
                .qmodii-legend-icon(:style="{ backgroundColor: props.chartColors.primary }")
                span {{ t("rangechart.currentvolume") }}:
                strong {{ comma(props.current) }}
            .qmodii-popover-item
                .qmodii-legend-icon(:style="{ backgroundColor: props.chartColors.secondary }")
                span {{ t("rangechart.averagevolume") }}:
                strong {{ comma(props.avg) }}
        .qmodii-popover-content(v-else)
            .qmodii-popover-item
                span {{ t("rangechart.currentprice") }}:
                strong {{ dec2(props.current) }}
</template>

<style lang="stylus">
.qmodii-range-chart-cont
    container-type inline-size
    container-name rangechart
    margin 0
    width 100%
    display flex
    flex-direction column
    gap var(--qmodii-spacing-lg)
    line-height 1.5

    .qmodii-range-chart-header
        display flex
        flex-direction column
        gap var(--qmodii-spacing-xs)

        .qmodii-range-chart-heading
            margin 0
            font-weight 700

        .qmodii-range-chart-description
            margin 0

    .qmodii-range-chart
        margin 0
        width 100%
        display flex
        justify-content center
        align-items flex-end
        overflow visible
        position relative

        .qmodii-bar-container
            display flex
            flex-direction column
            align-items center
            width 100%
            gap var(--qmodii-spacing-sm)

            .qmodii-labels
                display flex
                justify-content space-between
                width 100%

                .qmodii-label
                    display flex
                    flex-direction column

                    &.qmodii-label-high
                        align-items flex-end

                    .qmodii-label-text
                        font-size 0.85em
                        color var(--qmodii-secondary-text-color, #6D737A)

            .qmodii-legend
                display flex
                width 100%
                padding-top var(--qmodii-spacing-xs)
                gap var(--qmodii-spacing-md)

                .qmodii-legend-item
                    display flex
                    align-items center
                    gap var(--qmodii-spacing-xs)

                    .qmodii-legend-icon
                        width 7px
                        height 7px
                        border-radius 50%
                        background-color var(--qmodii-secondary-text-color, #6D737A)

                    .qmodii-legend-text
                        font-size 0.85em
                        color var(--qmodii-secondary-text-color, #6D737A)

    .qmodii-popover
        position absolute
        background-color white
        border 1px solid var(--qmodii-border-color, #E3E3E3)
        border-radius 4px
        padding var(--qmodii-spacing-xs)
        box-shadow 0 2px 8px rgba(0, 0, 0, 0.1)
        z-index 10

        .qmodii-popover-content
            font-size 0.85em
            color var(--qmodii-secondary-text-color, #6D737A)

            .qmodii-popover-item
                display flex
                align-items center
                gap var(--qmodii-spacing-xs)

                .qmodii-legend-icon
                    width 7px
                    height 7px
                    border-radius 50%
                    background-color var(--qmodii-secondary-text-color, #6D737A)
</style>

<style>
@container rangechart (max-width: 768px) {
    .qmodii-range-chart-cont {
        flex-direction: row;
        gap: var(--qmodii-spacing-lg);

        .qmodii-range-chart-header {
            min-width: 220px;
            justify-content: center;
        }
    }
}

@container rangechart (max-width: 480px) {
    .qmodii-range-chart-cont {
        .qmodii-range-chart-header {
            min-width: 150px;
        }
    }
}
</style>
